import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { client, urlFor } from "../sanity/client";

const VENDORS_QUERY = `*[_type == "vendor" && !(_id in path("drafts.**"))] | order(_createdAt desc) {
  _id, name, slug, category, logo, location, description, verified, rating, website
}`;

const categories = [
  { value: "all",          label: "All Vendors" },
  { value: "cables",       label: "Cables & Wires" },
  { value: "chargers",     label: "Chargers & Inverters" },
  { value: "solar",        label: "Solar" },
  { value: "batteries",    label: "Batteries & IoT" },
  { value: "spare-parts",  label: "Spare Parts" },
];

const categoryColors = {
  cables:        { color: "#2d6a4f", bg: "#eaf4ef", border: "#c2ddd0" },
  chargers:      { color: "#1e4d8c", bg: "#eaf0fb", border: "#bccfe9" },
  solar:         { color: "#8a5a0f", bg: "#faf3e5", border: "#e6cfa0" },
  batteries:     { color: "#4a3580", bg: "#f0ecfb", border: "#cdc3e8" },
  "spare-parts": { color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
};

function VendorCard({ vendor }) {
  const [hovered, setHovered] = useState(false);
  const theme = categoryColors[vendor.category] || categoryColors["chargers"];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "white", borderRadius: "16px",
        border: `1.5px solid ${hovered ? theme.color + "40" : "#ebebeb"}`,
        overflow: "hidden", transition: "all 0.22s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ? `0 16px 40px ${theme.color}15` : "0 2px 8px rgba(0,0,0,0.04)",
        cursor: "pointer",
      }}
    >
      {/* Logo area */}
      <div style={{
        height: "160px", background: theme.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", position: "relative",
      }}>
        {vendor.logo ? (
          <img
            src={urlFor(vendor.logo).width(200).height(120).fit("fill").url()}
            alt={vendor.name}
            style={{ width: "160px", height: "100px", objectFit: "contain" }}
          />
        ) : (
          <div style={{ fontSize: "48px", opacity: 0.3 }}>🏭</div>
        )}

        {/* Category badge */}
        <div style={{
          position: "absolute", top: "12px", left: "12px",
          background: "white", color: theme.color,
          fontSize: "10px", fontWeight: "800",
          padding: "4px 10px", borderRadius: "6px",
          fontFamily: "'Sora', sans-serif",
          letterSpacing: "0.5px", textTransform: "uppercase",
          border: `1px solid ${theme.border}`,
        }}>
          {categories.find(c => c.value === vendor.category)?.label || vendor.category}
        </div>

        {/* Verified badge */}
        {vendor.verified && (
          <div style={{
            position: "absolute", top: "12px", right: "12px",
            background: "#f0fdf4", color: "#16a34a",
            fontSize: "10px", fontWeight: "700",
            padding: "4px 10px", borderRadius: "6px",
            fontFamily: "'Sora', sans-serif",
            border: "1px solid #bbf7d0",
          }}>
            ✓ Verified
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: "18px 20px" }}>
        <h3 style={{
          fontFamily: "'Sora', sans-serif",
          fontSize: "15px", fontWeight: "800",
          color: "#1a1a2e", margin: "0 0 4px",
        }}>
          {vendor.name}
        </h3>

        {vendor.location && (
          <p style={{
            fontSize: "12px", color: "#9ca3af",
            fontFamily: "'DM Sans', sans-serif",
            margin: "0 0 8px",
          }}>
            📍 {vendor.location}
          </p>
        )}

        {vendor.description && (
          <p style={{
            fontSize: "13px", color: "#6b7280",
            fontFamily: "'DM Sans', sans-serif",
            lineHeight: "1.6", margin: "0 0 14px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}>
            {vendor.description}
          </p>
        )}

        {/* Rating */}
        {vendor.rating && (
          <div style={{ marginBottom: "14px" }}>
            {[1,2,3,4,5].map((star) => (
              <span key={star} style={{
                color: star <= vendor.rating ? "#f59e0b" : "#e5e7eb",
                fontSize: "14px",
              }}>★</span>
            ))}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <button style={{
            background: theme.bg, color: theme.color,
            border: `1.5px solid ${theme.border}`,
            borderRadius: "8px", padding: "7px 16px",
            fontSize: "12px", fontWeight: "700",
            cursor: "pointer", fontFamily: "'Sora', sans-serif",
            transition: "all 0.18s ease",
          }}
            onMouseEnter={(e) => { e.currentTarget.style.background = theme.color; e.currentTarget.style.color = "white"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = theme.bg; e.currentTarget.style.color = theme.color; }}
          >
            View Profile
          </button>

          {vendor.website && (
            <a href={vendor.website} target="_blank" rel="noreferrer"
              style={{ fontSize: "12px", color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>
              Visit Website ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Vendors() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const urlCategory = searchParams.get("category") || "all";
  const activeCategory = categories.find(c => c.value === urlCategory) ? urlCategory : "all";
  const setActiveCategory = (val) => setSearchParams(val === "all" ? {} : { category: val });

  useEffect(() => {
    client.fetch(VENDORS_QUERY)
      .then((data) => { setVendors(data); setLoading(false); })
      .catch((err) => { console.error(err); setLoading(false); });

    const subscription = client
      .listen(`*[_type == "vendor" && !(_id in path("drafts.**"))]`)
      .subscribe(() => {
        client.fetch(VENDORS_QUERY).then((data) => setVendors(data));
      });

    return () => subscription.unsubscribe();
  }, []);

  const filtered = vendors.filter((v) => {
    const matchCat = activeCategory === "all" || v.category === activeCategory;
    const matchSearch = v.name?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <main style={{ background: "#f8fafc", minHeight: "100vh" }}>

      {/* HEADER */}
      <div style={{ background: "white", borderBottom: "1px solid #e8eaed", padding: "40px 56px 32px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <p style={{ fontSize: "11px", fontWeight: "800", color: "#aab0b8", letterSpacing: "2px", textTransform: "uppercase", fontFamily: "'Sora', sans-serif", marginBottom: "10px" }}>
            VENDORS
          </p>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: "clamp(24px, 3vw, 36px)", fontWeight: "900", color: "#1a1a2e", letterSpacing: "-0.8px", margin: "0 0 8px" }}>
            Browse All Vendors
          </h1>
          <p style={{ color: "#6b7280", fontSize: "15px", fontFamily: "'DM Sans', sans-serif" }}>
            {vendors.length} verified vendors across India
          </p>
        </div>
      </div>

      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 56px" }}>

        {/* SEARCH + FILTER */}
        <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap", marginBottom: "32px" }}>
          <div style={{ position: "relative", flex: 1, minWidth: "240px" }}>
            <input type="text" placeholder="Search vendors..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ width: "100%", padding: "11px 44px 11px 16px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "14px", fontFamily: "'DM Sans', sans-serif", background: "white", outline: "none" }}
              onFocus={(e) => e.target.style.borderColor = "#2d6a4f"}
              onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
            />
            <span style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "16px" }}>🔍</span>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {categories.map((cat) => (
              <button key={cat.value} onClick={() => setActiveCategory(cat.value)}
                style={{
                  padding: "8px 16px", borderRadius: "8px",
                  border: `1.5px solid ${activeCategory === cat.value ? "#2d6a4f" : "#e5e7eb"}`,
                  background: activeCategory === cat.value ? "#2d6a4f" : "white",
                  color: activeCategory === cat.value ? "white" : "#374151",
                  fontSize: "12px", fontWeight: "700", cursor: "pointer",
                  fontFamily: "'Sora', sans-serif", transition: "all 0.18s ease", whiteSpace: "nowrap",
                }}>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* LOADING */}
        {loading && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ width: "40px", height: "40px", border: "3px solid #e5e7eb", borderTop: "3px solid #2d6a4f", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            <p style={{ color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>Loading vendors...</p>
          </div>
        )}

        {/* NO RESULTS */}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏭</div>
            <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: "20px", fontWeight: "800", color: "#1a1a2e", marginBottom: "8px" }}>No vendors found</h3>
            <p style={{ color: "#9ca3af", fontFamily: "'DM Sans', sans-serif" }}>Try a different category or search term</p>
          </div>
        )}

        {/* GRID */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px" }}>
            {filtered.map((vendor) => <VendorCard key={vendor._id} vendor={vendor} />)}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );
}

export default Vendors;